import React from "react";
import { INavBarItem } from "../../interfaces/others/INavBarItem";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { IRoute } from "../../interfaces/others/IRoute";
import { useSelector } from "react-redux";
import { RootState } from "../../store/index";
import { can } from "../../auth/can.ts";

const NavBarItem: React.FC<
  INavBarItem & { childrenRoutes?: IRoute[]; isNew?: boolean }
> = ({
  active,
  name,
  to,
  activeKey,
  icon,
  childrenRoutes,
  isNew,
  disabled,
}) => {
  const { user, partner } = useSelector((state: RootState) => state.auth);
  const { isDirectBoss, bossId } = useSelector(
    (state: RootState) => state.directBoss as any
  );

  const isVacationsResponsible = partner?.isVacationsResponsible === true;

  const canAccessChild = (child: IRoute) =>
    Boolean(child.requiredPermission) && can(child.requiredPermission, user);

  const filteredChildren = childrenRoutes?.filter((child) => canAccessChild(child));

  const hasChildren =
    Array.isArray(filteredChildren) && filteredChildren.length > 0;

  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const timeoutRef = React.useRef<number | null>(null);

  const computedTo = React.useMemo(() => {
    if (isDirectBoss && (bossId || partner?.id)) {
      const directBossRoots = ["/incapacidades", "/permisos", "/vacaciones", "/incidencias"];
      if (directBossRoots.includes(to)) {
        return `${to}/${bossId ?? partner?.id}`;
      }
    }

    if (
      isVacationsResponsible &&
      !isDirectBoss &&
      to.includes("vacaciones")
    ) {
      return "/vacaciones";
    }

    return to;
  }, [to, isDirectBoss, isVacationsResponsible, partner?.id, bossId]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = window.setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const canShowNavItem = () => {
    if (childrenRoutes) {
      return hasChildren;
    }
    return true;
  };

  if (!canShowNavItem()) {
    return null;
  }

  if (hasChildren) {
    return (
      <div
        ref={wrapperRef}
        className={`nav-item dropdown${open ? " show" : ""}`}
        onMouseEnter={disabled ? undefined : handleMouseEnter}
        onMouseLeave={disabled ? undefined : handleMouseLeave}
      >
        <Link
          to={computedTo}
          className={`${
            active.includes(activeKey)
              ? "nav-link active dropdown-toggle"
              : "nav-link dropdown-toggle"
          }${disabled ? " disabled" : ""}`}
          aria-expanded={open}
          aria-haspopup="true"
          id={`dropdown-${activeKey}`}
          style={{
            textDecoration: "none",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
          }}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            setOpen((prev) => !prev);
            navigate(computedTo);
          }}
        >
          <i className={`fa-solid fa-${icon} me-1`}></i>
          {name}
          {filteredChildren?.some((child) => child.isNew) && (
            <span className="new-badge">New</span>
          )}
        </Link>
        <ul
          className={`dropdown-menu${open ? " show" : ""}`}
          aria-labelledby={`dropdown-${activeKey}`}
        >
          {filteredChildren!.map((child) => (
            <li key={child.to}>
              <Link
                to={child.to}
                className="dropdown-item"
                style={{ cursor: "pointer" }}
                onClick={() => setOpen(false)}
              >
                <i className={`fa-solid fa-${child.icon} me-2`}></i>
                {child.name}
                {child.isNew && <span className="new-badge">New</span>}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <Link
      to={computedTo}
      style={{ textDecoration: "none" }}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
          return;
        }
      }}
    >
      <a
        className={`${
          active.includes(activeKey) ? "nav-link active" : "nav-link"
        }${disabled ? " disabled" : ""}`}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <i className={`fa-solid fa-${icon} me-1`}></i>
        {name}
        {isNew && <span className="new-badge">New</span>}
      </a>
    </Link>
  );
};

export default NavBarItem;
